import { type ClientConfig } from "src/client-config/getClientConfig.ts";
import { routes } from "src/constants/routes.constants.ts";
import { ErpId } from "src/constants/constants.ts";
import { redirect } from "react-router-dom";
import { getModules } from "src/system/getModules.ts";
import { getModuleRouteObjects } from "src/system/getModuleRouteObjects.tsx";

const loginPageRoute = {
  path: routes.root.root,
  lazy: () => import("src/auth/pages/LoginPage/LoginPage.tsx"),
};

const notFoundRoute = {
  path: "*",
  lazy: () => import("src/admin/not-found/not-found.tsx"),
};

const billRoute = {
  path: routes.admin.bill.root,
  lazy: () => import("src/admin/bill/bill.tsx"),
  children: [
    {
      index: true,
      lazy: () => import("src/admin/bill/balance/Balance.tsx"),
    },
    {
      path: routes.admin.bill.account_movements.root,
      lazy: () =>
        import("src/admin/bill/account-movements/AccountMovements.tsx"),
    },
  ],
};

const dashboardRoute = {
  path: routes.admin.dashboard.root,
  lazy: () => import("src/admin/dashboard/dashboard.tsx"),
};

const basicConfigurationRoute = {
  index: true,
  lazy: () => import("src/admin/administration/tabs/BasicConfigurationTab.tsx"),
};

const systemTabRoute = {
  path: routes.admin.administration.system.root,
  lazy: () => import("src/admin/administration/tabs/SystemTab/SystemTab.tsx"),
};

const recommendationSettingsRoute = {
  path: routes.admin.administration.recommendation.root,
  lazy: () =>
    import(
      "src/admin/administration/tabs/RecommendationSettingsTab/RecommendationSettingsTab.tsx"
    ),
};

const reminderSettingsRoute = {
  path: routes.admin.administration.reminders.root,
  lazy: () =>
    import(
      "src/admin/administration/tabs/ReminderSettingsTab/ReminderSettingsTab.tsx"
    ),
};

const marketingProviderRoute = {
  path: routes.admin.administration.marketing.root,
  lazy: () =>
    import(
      "src/admin/administration/tabs/MarketingProviderTab/MarketingProviderTab.tsx"
    ),
};

const debugRoute = {
  path: routes.admin.administration.debug.root,
  lazy: () => import("src/admin/administration/tabs/DebugTab.tsx"),
};

const importProductsRoute = {
  lazy: () => import("src/admin/import/import.tsx"),
  path: routes.admin.import.root,
  children: [
    {
      index: true,
      lazy: () => import("src/admin/import/form/import-products.route.tsx"),
    },
  ],
};

const pickingRoute = {
  lazy: () => import("src/admin/picking/picking.route.tsx"),
  path: routes.admin.picking.root,
};

const accountingExportRoute = {
  lazy: () => import("src/admin/accounting-export/export.tsx"),
  path: routes.admin.accountingExport.root,
  children: [
    {
      index: true,
      lazy: () =>
        import("src/admin/accounting-export/form/accounting-export.route.tsx"),
    },
  ],
};

const balanceListRoute = {
  lazy: () => import("src/admin/balance-list/BalanceList.layout.tsx"),
  path: routes.admin.balanceList.root,
  children: [
    {
      index: true,
      lazy: () => import("src/admin/balance-list/BalanceList.route.tsx"),
    },
  ],
};

const organizerOverviewRoute = {
  lazy: () => import("src/admin/organizer/overview.tsx"),
  path: routes.admin.organizer.overview.root,
};

const organizerEmailRoute = {
  lazy: () => import("src/admin/organizer/email.tsx"),
  path: routes.admin.organizer.email.root,
};

const organizerCalendarRoute = {
  lazy: () => import("src/admin/organizer/calendar.tsx"),
  path: routes.admin.organizer.calendar.root,
};

const organizerNotesRoute = {
  lazy: () => import("src/admin/organizer/notes.tsx"),
  path: routes.admin.organizer.notes.root,
};

const organizerAddressbookRoute = {
  lazy: () => import("src/admin/organizer/addressbook.tsx"),
  path: routes.admin.organizer.addressbook.root,
};

const organizerTasksRoute = {
  lazy: () => import("src/admin/organizer/tasks.tsx"),
  path: routes.admin.organizer.tasks.root,
};

const organizerProjectsRoute = {
  lazy: () => import("src/admin/organizer/projects.tsx"),
  path: routes.admin.organizer.projects.root,
};

const organizerTicketsRoute = {
  lazy: () => import("src/admin/organizer/tickets.tsx"),
  path: routes.admin.organizer.tickets.root,
};

export const buildRoutes = ({ config }: { config: ClientConfig }) => {
  return getErpRoutes(config);
};

// Админка EXONN над установками клиентов — только на установке EXONN.
const exonnInstallationsRoute = {
  path: routes.admin.exonn.installations.root,
  lazy: () => import("src/admin/exonn-installations/InstallationsPage.tsx"),
};

const exonnEpostRoute = {
  path: routes.admin.exonn.epost.root,
  lazy: () => import("src/admin/exonn-epost/EpostPage.tsx"),
  children: [
    {
      index: true,
      lazy: () => import("src/admin/exonn-epost/tabs/InstallationsTab.tsx"),
    },
    {
      path: routes.admin.exonn.epost.prices.root,
      lazy: () => import("src/admin/exonn-epost/tabs/PricesTab.tsx"),
    },
    {
      path: routes.admin.exonn.epost.letters.root,
      lazy: () => import("src/admin/exonn-epost/tabs/LettersTab.tsx"),
    },
  ],
};

const getErpRoutes = (config: ClientConfig) => {
  const modules = getModules();
  const erpAdministration = {
    path: routes.admin.administration.root,
    lazy: () => import("src/admin/administration/administration.tsx"),
    children: [
      basicConfigurationRoute,
      systemTabRoute,
      recommendationSettingsRoute,
      reminderSettingsRoute,
      marketingProviderRoute,
      debugRoute,
    ],
  };
  return [
    {
      path: routes.admin.root,
      lazy: () => import("src/admin/layout/admin.layout.tsx"),
      children: [
        dashboardRoute,
        erpAdministration,
        ...(config.erpId === ErpId.ExonnErp
          ? [exonnInstallationsRoute, exonnEpostRoute]
          : []),
        billRoute,
        pickingRoute,
        importProductsRoute,
        accountingExportRoute,
        balanceListRoute,
        organizerOverviewRoute,
        organizerEmailRoute,
        organizerCalendarRoute,
        organizerNotesRoute,
        organizerAddressbookRoute,
        organizerTasksRoute,
        organizerProjectsRoute,
        organizerTicketsRoute,
        ...modules.flatMap((module) =>
          getModuleRouteObjects({ module, clientConfig: config }),
        ),
        notFoundRoute,
      ],
    },
    loginPageRoute,
    {
      path: "*",
      loader: () => {
        return redirect(routes.admin.root);
      },
    },
  ];
};
